import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { Package, ShoppingCart, CreditCard, User, Calendar, MapPin, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [statsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/orders/`)
      ]);
      
      setStats(statsRes.data);
      setOrders(ordersRes.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Dashboard error:', error);
      }
      toast.error('Gagal memuat data dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchDashboardData();
    }
  }, [user, fetchDashboardData]);

  const getStatusBadge = (status) => {
    const variants = {
      pending: { variant: 'secondary', text: 'Menunggu' },
      confirmed: { variant: 'default', text: 'Dikonfirmasi' },
      processing: { variant: 'default', text: 'Diproses' },
      completed: { variant: 'default', text: 'Selesai' },
      cancelled: { variant: 'destructive', text: 'Dibatalkan' }
    };
    
    const config = variants[status] || variants.pending;
    return <Badge variant={config.variant}>{config.text}</Badge>;
  };

  const getPaymentStatusBadge = (status) => {
    const variants = {
      pending: { className: 'bg-yellow-100 text-yellow-800', text: 'Menunggu Pembayaran' },
      dp: { className: 'bg-blue-100 text-blue-800', text: 'DP Dibayar' },
      paid: { className: 'bg-green-100 text-green-800', text: 'Lunas' },
      failed: { className: 'bg-red-100 text-red-800', text: 'Gagal' },
      expired: { className: 'bg-gray-100 text-gray-800', text: 'Kadaluarsa' }
    };
    
    const config = variants[status] || variants.pending;
    return <Badge className={config.className}>{config.text}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen py-12 flex items-center justify-center">
        <p className="text-muted-foreground">Memuat dashboard...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold font-['Outfit'] mb-2">Dashboard</h1>
          <p className="text-muted-foreground">
            Selamat datang, {user?.full_name || 'User'}!
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="rounded-2xl">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardDescription className="text-sm">Total Pesanan</CardDescription>
                    <CardTitle className="text-3xl font-bold font-['Outfit'] mt-2">
                      {stats?.total_orders || 0}
                    </CardTitle>
                  </div>
                  <Package className="h-8 w-8 text-primary" />
                </div>
              </CardHeader>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <Card className="rounded-2xl">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardDescription className="text-sm">Menunggu</CardDescription>
                    <CardTitle className="text-3xl font-bold font-['Outfit'] mt-2">
                      {stats?.pending_orders || 0}
                    </CardTitle>
                  </div>
                  <Clock className="h-8 w-8 text-yellow-500" />
                </div>
              </CardHeader>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <Card className="rounded-2xl">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardDescription className="text-sm">Selesai</CardDescription>
                    <CardTitle className="text-3xl font-bold font-['Outfit'] mt-2">
                      {stats?.completed_orders || 0}
                    </CardTitle>
                  </div>
                  <CreditCard className="h-8 w-8 text-green-500" />
                </div>
              </CardHeader>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
          >
            <Card className="rounded-2xl">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardDescription className="text-sm">Total Belanja</CardDescription>
                    <CardTitle className="text-2xl font-bold font-['Outfit'] mt-2">
                      Rp {(stats?.total_spent || 0).toLocaleString('id-ID')}
                    </CardTitle>
                  </div>
                  <ShoppingCart className="h-8 w-8 text-primary" />
                </div>
              </CardHeader>
            </Card>
          </motion.div>
        </div>

        {/* Orders List */}
        <div className="grid grid-cols-1 gap-6">
          <Card className="rounded-2xl">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-['Outfit']">Pesanan Saya</CardTitle>
                  <CardDescription>Daftar semua pesanan umrah Anda</CardDescription>
                </div>
                <Link to="/packages">
                  <Button className="rounded-full">
                    <Package className="h-4 w-4 mr-2" />
                    Pesan Paket Baru
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {orders.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-lg font-medium mb-2">Belum ada pesanan</p>
                  <p className="text-sm text-muted-foreground mb-4">
                    Mulai perjalanan spiritual Anda dengan memesan paket umrah
                  </p>
                  <Link to="/packages">
                    <Button className="rounded-full">
                      Lihat Paket Umrah
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {orders.map((order) => (
                    <motion.div
                      key={order.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="border border-border rounded-xl p-6 hover:shadow-md transition-shadow"
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-lg">
                              {order.package?.package_name || 'Paket Umrah'}
                            </h3>
                            {getStatusBadge(order.booking_status)}
                            {!['cancelled', 'completed'].includes(order.booking_status) && getPaymentStatusBadge(order.payment_status)}
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-muted-foreground">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4" />
                              <span>Order: {order.order_number}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4" />
                              <span>{order.total_pilgrims} Jamaah</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <CreditCard className="h-4 w-4" />
                              <span className="font-semibold text-foreground">
                                Rp {order.total_amount.toLocaleString('id-ID')}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-col gap-2">
                          <Link to={`/order/${order.id}`}>
                            <Button variant="outline" className="w-full rounded-full">
                              Lihat Detail
                            </Button>
                          </Link>
                          {order.payment_status === 'pending' && (
                            <Link to={`/order/${order.id}`}>
                              <Button className="w-full rounded-full bg-primary">
                                Bayar Sekarang
                              </Button>
                            </Link>
                          )}
                          {order.payment_status === 'dp' && (
                            <Link to={`/order/${order.id}`}>
                              <Button className="w-full rounded-full bg-blue-600 hover:bg-blue-700 text-white">
                                Lunasi Sekarang
                              </Button>
                            </Link>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Profile Card */}
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="font-['Outfit']">Profil Saya</CardTitle>
              <CardDescription>Informasi akun Anda</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="font-medium">{user?.full_name}</p>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                </div>
              </div>
              <div className="pt-2">
                <p className="text-sm text-muted-foreground">
                  Role: <span className="font-medium capitalize text-foreground">{user?.role}</span>
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  No. Telepon: <span className="font-medium text-foreground">{user?.phone || '-'}</span>
                </p>
                <div className="mt-3">
                  <Link to="/affiliate">
                    <Button variant="outline" className="w-full rounded-full text-sm">
                      Program Affiliate
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ClientDashboard;